import { API_BASE_URL } from './constants';
import { header3DefinitionJSON } from './pages/StartPage';

export type AuthenticationData = {
  token: string;
  userId: string;
  customerId?: string;
  tfaMode?: string;
  user?: {
    userDetailId?: string;
    firstName?: string;
    familyName?: string;
  };
  userDetailId?: string;
  firstName?: string;
  familyName?: string;
};

const notifyUserNameUpdated = () => window.dispatchEvent(new Event('user-name-updated'));
const notifySidebarModeUpdated = () => window.dispatchEvent(new Event('sidebar-mode-updated'));

const resolvePatientId = (patients: unknown): string => {
  if (!Array.isArray(patients) || patients.length === 0) return '';
  const first = patients[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') {
    const candidate = (first as { patientId?: unknown; id?: unknown }).patientId ??
      (first as { id?: unknown }).id;
    return typeof candidate === 'string' ? candidate : '';
  }
  return '';
};

const resolveAdminLevel = async (userId: string, token: string) => {
  try {
    const response = await fetch(`${API_BASE_URL}/user/admin-level/${userId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!response.ok) {
      localStorage.setItem('loginAdminLevel', '');
      localStorage.setItem('sidebarMode', 'modeLegacy');
      notifySidebarModeUpdated();
      return;
    }

    const adminLevelText = (await response.text()).trim();
    const adminLevel = Number(adminLevelText);
    if (Number.isFinite(adminLevel) && adminLevel >= 7) {
      localStorage.setItem('sidebarMode', 'modeRoot');
    } else if (Number.isFinite(adminLevel) && adminLevel >= 5) {
      localStorage.setItem('sidebarMode', 'modeAdmin');
    } else {
      localStorage.setItem('sidebarMode', 'modePatient');
    }
    localStorage.setItem('loginAdminLevel', adminLevelText);
    notifySidebarModeUpdated();
  } catch (error) {
    console.error('Failed to load admin level', error);
    localStorage.setItem('loginAdminLevel', '');
    localStorage.setItem('sidebarMode', 'modeLegacy');
    notifySidebarModeUpdated();
  }
};

export const completeAuthenticatedLogin = async (data: AuthenticationData, userName: string) => {

  console.log("completeAuthenticatedLogin: userName=" + userName + " token=" + data.token);

  localStorage.setItem('headerDefinition', header3DefinitionJSON);
  localStorage.setItem('loginAdminLevel', '');
  localStorage.setItem('sidebarMode', 'modeLegacy');
  localStorage.setItem('token', data.token);
  localStorage.setItem('tfaMode', data.tfaMode ?? 'none');
  localStorage.setItem('loginUserId', data.userId);
  localStorage.setItem('customerId', data.customerId ?? '');
  localStorage.setItem('loginUserName', userName);
  localStorage.removeItem('userFullName');
  localStorage.removeItem('currentAppointmentChain');
  notifySidebarModeUpdated();
  notifyUserNameUpdated();

  await resolveAdminLevel(data.userId, data.token);

  try {
    const response = await fetch(`${API_BASE_URL}/patients/user/${data.userId}`, {
      headers: { Authorization: `Bearer ${data.token}` },
    });
    localStorage.setItem('loginPatientId', response.ok ? resolvePatientId(await response.json()) : '');
  } catch (error) {
    console.error('Failed to load patient details', error);
    localStorage.setItem('loginPatientId', '');
  }

  const user = data.user ?? data;
  let resolvedFullName = `${user.firstName ?? ''} ${user.familyName ?? ''}`.replace(/\s+/g, ' ').trim();
  if (user.userDetailId) {
    try {
      const response = await fetch(`${API_BASE_URL}/user-details/${user.userDetailId}`, {
        headers: { Authorization: `Bearer ${data.token}` },
      });
      if (response.ok) {
        const detail = await response.json();
        if (detail?.title) resolvedFullName = `${detail.title} ${resolvedFullName}`.trim();
      }
    } catch (error) {
      console.error('Failed to load user details', error);
    }
  }

  localStorage.setItem('suppressLiveChat', 'false');
  localStorage.setItem('userFullName', resolvedFullName);
  notifyUserNameUpdated();
};
